import React from 'react';
import dayjs from 'dayjs';
import {
  DeliveryTimeline,
  type DeliveryEvent,
  type DeliveryState,
} from '@/design-system/delivery-timeline';
import {
  PackageStatus,
  PackageTimelineEvent,
  PackageTimelineRider,
} from '@/services/api/packages';
import { STATUS_LABELS } from './PackageBadges';

// Back-office driver profile base — the timeline links a rider to their page
// here (e.g. .../driver/9786), matching the id the backend resolves.
const DRIVER_PROFILE_BASE = 'https://back-office.dashelectric.co/driver';

// The handover-scan actor is a rider: render `CODE - name` linked to the
// driver page instead of the raw rider id. Falls back gracefully when the
// snapshot lacked a code or name.
const riderActor = (rider: PackageTimelineRider): React.ReactNode => {
  const label = [rider.code, rider.name].filter(Boolean).join(' - ') || `#${rider.id}`;
  return (
    <span>
      oleh{' '}
      <a
        href={`${DRIVER_PROFILE_BASE}/${rider.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-accent hover:underline"
      >
        {label}
      </a>
    </span>
  );
};

const humanize = (status: string) =>
  STATUS_LABELS[status as PackageStatus] ??
  status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// The newest (last) event carries the item's current status. Terminal outcomes
// render as reached/failed; anything mid-journey is the purple "you are here".
// `LOST` is *not* terminal in v2 (a found parcel re-enters at the hub), so it
// stays "current" — the journey isn't over.
const DONE_STATUSES: readonly string[] = [
  'COMPLETED',
  'RETURNED_TO_HUB',
  'RETURNED_TO_CLIENT',
  // Legacy equivalents (pre-migration history rows)
  'DELIVERED',
  'RETURNED',
];

const lastState = (status: string): DeliveryState => {
  if (status === 'CANCELLED') return 'failed';
  if (DONE_STATUSES.includes(status)) return 'done';
  return 'current';
};

interface PackageTimelineProps {
  events: PackageTimelineEvent[];
}

// Maps the item's status-history (oldest-first) to the kit DeliveryTimeline.
// Chronological top-to-bottom; the last node reflects the current status.
const PackageTimeline: React.FC<PackageTimelineProps> = ({ events }) => {
  const timelineEvents: DeliveryEvent[] = events.map((e, i) => ({
    key: e.id,
    title: humanize(e.toStatus),
    state: i === events.length - 1 ? lastState(e.toStatus) : 'done',
    time: dayjs(e.changedAt).format('DD MMM YYYY, HH:mm'),
    description: e.note || undefined,
    location: e.rider
      ? riderActor(e.rider)
      : e.changedBy
        ? `oleh ${e.changedBy}`
        : undefined,
  }));

  return <DeliveryTimeline events={timelineEvents} />;
};

export default PackageTimeline;
